import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/inputs';
import { ListSkeleton } from '@/components/PageSkeleton';
import { ErrorState } from '@/components/kit';
import { PRODUCT_FIELDS } from '@/shared/fields';
import { BUILT_IN_ANCHORS, featureList, isBuiltIn, isPrivate, productPath } from '@/shared/products';
import { money, planPriceList, productTerms } from '@/shared/pricing';
import { breadcrumbSchema, productSchema, useJsonLd, useSeo } from '@/shared/seo';
import { rows } from '@/shared/data';
import { GLOW, LABEL, WRAP } from '@/components/home/styles';
import { cn } from '@/lib/utils';
import { useT } from '@/shared/i18n';
import { P } from '@/shared/locales/pages/keys';

const { eq } = window.ApperSDK.CoreSDK;

export const route = { path: '/p/:slug', layout: 'public', access: 'public' };

const BUILT_IN_CTA = {
  Demon: P.ctaDemon,
  Wraith: P.ctaWraith,
  Telly: P.ctaTelly,
};

/** Prices a product sells at, in USD: built-in plans read the site's price tables. */
const pricesFor = (product) =>
  isBuiltIn(product) ? planPriceList(product.plan_key_c) : productTerms(product).map((t) => t.price);

/** Title, description and structured data. Products hidden from the front page stay out of search. */
function usePageMeta(product) {
  const listed = !!product?.active_c && !!product?.show_on_home_c;
  const description = product
    ? [product.tagline_c, product.description_c].filter(Boolean).join('. ') || `${product.Name} from Ghost Cable.`
    : '';

  useSeo(
    product
      ? {
          title: product.Name,
          description,
          path: productPath(product),
          image: product.image_url_c || undefined,
          type: 'product',
          noindex: !listed,
        }
      : null,
  );

  useJsonLd(
    'product',
    product &&
      listed && [
        productSchema({
          name: product.Name,
          description,
          path: productPath(product),
          image: product.image_url_c,
          prices: pricesFor(product),
        }),
        breadcrumbSchema([
          { name: 'Ghost Cable', path: '/' },
          { name: product.Name, path: productPath(product) },
        ]),
      ],
  );
}

function Unavailable({ title, body }) {
  const { t } = useT();
  return (
    <div className={cn(WRAP, 'pb-24 pt-24 md:pt-32')}>
      <p className={cn(LABEL, 'flex items-center gap-2')}>
        <ApperIcon name="Ghost" size={13} />
        {t(P.nothing)}
      </p>
      <h1 className="mt-4 text-[clamp(38px,6vw,64px)] font-light leading-[1.05]">{title}</h1>
      <p className="mt-4 max-w-lg text-[15px] leading-7 text-muted-foreground">{body}</p>
      <Button size="lg" className="mt-10" asChild>
        <Link to="/#plans">{t(P.seePlans)}</Link>
      </Button>
    </div>
  );
}

function Features({ items }) {
  const { t } = useT();
  if (!items.length) return null;
  return (
    <ul className="mt-8 space-y-2.5 text-[15px] text-foreground/80">
      {items.map((f) => (
        <li key={f} className="flex items-start gap-3">
          <ApperIcon name="Check" size={16} className="mt-1 shrink-0 text-primary" />
          {t(f)}
        </li>
      ))}
    </ul>
  );
}

export default function ProductPage() {
  const { t } = useT();
  const { slug } = useParams();
  const [term, setTerm] = useState(null);

  const { data: product, loading, error, run } = useFetch(
    () =>
      sdk
        .table('products_c')
        .select(PRODUCT_FIELDS)
        .where(eq('slug_c', slug))
        .limit(1)
        .fetch()
        .then(rows)
        // Private plans live on their own unlisted page, never under /p/.
        .then((list) => (list[0] && !isPrivate(list[0]) ? list[0] : null)),
    [slug],
  );

  usePageMeta(product);

  const terms = product ? productTerms(product) : [];
  const current = terms.find((x) => x.id === term) ?? terms[terms.length - 1] ?? null;

  useEffect(() => {
    setTerm(null);
  }, [slug]);

  if (loading && product === undefined) {
    return (
      <div className={cn(WRAP, 'pb-24 pt-24')}>
        <ListSkeleton rows={4} />
      </div>
    );
  }
  if (error) {
    return (
      <div className={cn(WRAP, 'pb-24 pt-24')}>
        <ErrorState message={error} onRetry={run} />
      </div>
    );
  }
  if (!product) {
    return (
      <Unavailable
        title={t(P.notHere)}
        body={t(P.oldLink)}
      />
    );
  }
  if (!product.active_c) {
    return (
      <Unavailable
        title={t(P.unavailable, { name: product.Name })}
        body={t(P.paused)}
      />
    );
  }

  const features = featureList(product);
  const builtIn = isBuiltIn(product);

  return (
    <div className={cn(WRAP, 'pb-24 pt-16 md:pt-24')}>
      <p className={cn(LABEL, 'flex items-center gap-2')}>
        <ApperIcon name="Sparkles" size={13} />
        {builtIn ? t(P.gcPlan) : t(P.fromHouse)}
      </p>
      <h1 className={cn('mt-4 font-heading text-[clamp(64px,11vw,140px)] leading-[0.9]', GLOW)}>{product.Name}</h1>
      {product.tagline_c && <p className="mt-5 text-xl text-foreground/85">{t(product.tagline_c)}</p>}

      <div className="mt-14 grid gap-14 md:grid-cols-[1.15fr_1fr] md:gap-20">
        <div className="border-t border-border pt-10">
          {product.image_url_c && (
            <img
              src={product.image_url_c}
              alt={product.Name}
              className="mb-8 aspect-video w-full rounded-lg border border-border object-cover"
              loading="lazy"
            />
          )}
          {product.description_c && (
            <p className="max-w-xl whitespace-pre-line text-[15px] leading-7 text-muted-foreground">
              {t(product.description_c)}
            </p>
          )}
          <Features items={features} />
        </div>

        <div className="border-t border-border pt-10">
          {builtIn ? (
            <>
              <p className={LABEL}>{t(P.pricesOptions)}</p>
              <p className="mt-4 max-w-md text-[15px] leading-7 text-muted-foreground">
                {t(P.pickOnPlans)}
              </p>
              <Button size="lg" className="mt-8" asChild>
                <Link to={BUILT_IN_ANCHORS[product.plan_key_c] ?? '/#plans'}>
                  {t(BUILT_IN_CTA[product.plan_key_c] ?? P.seePricesBtn)}
                </Link>
              </Button>
            </>
          ) : terms.length === 0 ? (
            <>
              <p className={LABEL}>{t(P.pricing)}</p>
              <p className="mt-4 max-w-md text-[15px] leading-7 text-muted-foreground">
                {t(P.onWay)}
              </p>
              <Button size="lg" variant="outline" className="mt-8" asChild>
                <Link to="/#contact">{t(P.contactUs)}</Link>
              </Button>
            </>
          ) : (
            <>
              <p className={LABEL}>{t('Term')}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {terms.map((x) => (
                  <Pill key={x.id} on={current?.id === x.id} onClick={() => setTerm(x.id)}>
                    {t(x.label)}
                  </Pill>
                ))}
              </div>
              <div className="mt-10 flex flex-wrap items-end gap-4">
                <span className={cn('text-7xl font-light leading-none', GLOW)}>{money(current.price)}</span>
                <span className="pb-2 text-sm text-muted-foreground">
                  USD · {t(current.label)}
                  {current.months > 1 && (
                    <>
                      <br />≈ {t('{price}/mo', { price: money(Math.round((current.price / current.months) * 100) / 100) })}
                    </>
                  )}
                </span>
              </div>
              <Button size="lg" className="mt-10" asChild>
                <Link to={`/checkout?plan=Product&product=${product.Id}&term=${current.id}`}>{t(P.order, { name: product.Name })}</Link>
              </Button>
              <p className="mt-4 text-xs text-muted-foreground">
                {current.months === 0 ? `${t(P.paidOnce)} ` : ''}
                {t(P.noCharge)}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
